/**
 * Shared UI component for UI Dialog.
 * PARENT-CHILD CONTRACT: parent screens provide the data and configuration that
 * this wrapper renders or normalizes for reuse across the application.
 */
import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import { DialogModule } from 'primeng/dialog';

import { UiButtonComponent } from '../ui-button/ui-button.component';
import { UiLabelTranslatePipe } from '../ui-label-translate.pipe';
import { UiDialogTone } from './ui-dialog.types';

/**
 * COMPONENT: Ui Dialog
 * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
 * Output: Returns the documented value or updates component/service state without changing the public contract.
 * Processing: Documents the Angular declaration that connects this class to templates, dependency injection, or template transforms.
 */
@Component({
  selector: 'ui-dialog',
  standalone: true,
  imports: [DialogModule, UiButtonComponent, UiLabelTranslatePipe],
  templateUrl: './ui-dialog.component.html',
  styleUrl: './ui-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiDialogComponent {
  /** PARENT-CHILD CONTRACT: open documents the input state boundary used by this file. */
  readonly open = input(false);
  /** PARENT-CHILD CONTRACT: title documents the input state boundary used by this file. */
  readonly title = input('');
  /** PARENT-CHILD CONTRACT: description documents the input state boundary used by this file. */
  readonly description = input('');
  readonly tone = input<UiDialogTone>('default');
  /** PARENT-CHILD CONTRACT: confirmLabel documents the input state boundary used by this file. */
  readonly confirmLabel = input('Yes');
  /** PARENT-CHILD CONTRACT: cancelLabel documents the input state boundary used by this file. */
  readonly cancelLabel = input('No');
  readonly confirmAction = output<void>();
  readonly cancelAction = output<void>();

  /**
   * BUSINESS LOGIC: onVisibleChange
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  onVisibleChange(visible: boolean): void {
    if (!visible) {
      this.cancelAction.emit();
    }
  }
}
